'use client';

import { StoneFAQ, type StoneFAQItem } from './StoneFAQ';

export type StoneFAQSource = 'home' | 'valuations' | 'about' | 'contact';

export interface StoneFAQSectionProps {
  messages?: Record<string, unknown>;
  locale?: string;
  /** Which FAQ cluster to render from messages.stone.faqs */
  source?: StoneFAQSource;
  className?: string;
  /** Optional direct FAQ items (overrides messages) */
  faqs?: StoneFAQItem[];
  title?: string;
  description?: string;
}

function readFaqCluster(
  messages: Record<string, unknown> | undefined,
  source: StoneFAQSource,
): { title?: string; description?: string; items: StoneFAQItem[] } {
  const stone = (messages?.stone || {}) as Record<string, unknown>;
  const faqsRoot = (stone.faqs || {}) as Record<string, unknown>;
  const cluster = (faqsRoot[source] || {}) as {
    title?: string;
    description?: string;
    items?: StoneFAQItem[];
  };

  const items = Array.isArray(cluster.items)
    ? cluster.items.filter((item) => item?.question && item?.answer)
    : [];

  return {
    ...(cluster.title ? { title: cluster.title } : {}),
    ...(cluster.description ? { description: cluster.description } : {}),
    items,
  };
}

/**
 * Theme-renderable FAQ section that pulls copy from stone i18n messages.
 */
export function StoneFAQSection({
  messages,
  locale = 'en',
  source = 'home',
  className = '',
  faqs: faqsProp,
  title: titleProp,
  description: descriptionProp,
}: StoneFAQSectionProps) {
  const fromMessages = readFaqCluster(messages, source);
  const faqs = faqsProp?.length ? faqsProp : fromMessages.items;

  if (!faqs.length) {
    return null;
  }

  const title = titleProp ?? fromMessages.title;
  const description = descriptionProp ?? fromMessages.description;

  return (
    <StoneFAQ
      faqs={faqs}
      {...(title ? { title } : {})}
      {...(description ? { description } : {})}
      locale={locale}
      className={className || 'bg-stone-50'}
    />
  );
}

export default StoneFAQSection;
